import React, { useCallback, useEffect, useState } from "react";
import useSearchStore from "@/stores/useSearchStore";
import { buildCompanySelection, buildSearchRequestBody } from "@/utils/searchRequestBuilder.util";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { ListConfigurationDto } from "@advanced-sales-technologies/backend-client/models/ListConfigurationDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useUpdateSelectOption } from "@/hooks/api/list/useUpdateSelectOption";
import { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import HasMoreDataCellBase, { HasMoreDataCellBaseProps } from "@/components/molecules/table/HasMoreDataCellBase";
import SelectAsyncComboBox from "@/components/organisms/SelectAsyncComboBox";

type HasMoreDataCellProps<OptionTypeGeneric extends { id: string }> = {
  initialSelected?: string | undefined;
  queryKey: readonly unknown[];
  company: CompanyEntryDto;
  searchParams?: SearchParams;
  listId?: string | undefined | null;
  target: SelectOptionRequestDto.target;
  placeholder: string;
  useQueryHook: (
    query: string | null,
    enabled: boolean,
    convertedSearchParams: ListConfigurationDto,
  ) => UseQueryResult<OptionTypeGeneric[], Error>;
  renderOption: (option: OptionTypeGeneric, isSelected: boolean) => React.ReactNode;
  buildDisplayValue: (option: OptionTypeGeneric) => string;
} & Omit<HasMoreDataCellBaseProps, "isPending" | "onOpenChange">;

const HasMoreDataCellGeneral = <OptionTypeGeneric extends { id: string }>({
  initialSelected,
  displayValue,
  queryKey,
  company,
  searchParams,
  listId,
  target,
  hasMoreData,
  placeholder,
  useQueryHook,
  renderOption,
  buildDisplayValue,
  renderValue = (value) => <>{value}</>,
}: HasMoreDataCellProps<OptionTypeGeneric>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const [selectedId, setSelectedId] = useState<string | undefined>(initialSelected);
  const [convertedSearchParams, setConvertedSearchParams] = useState<ListConfigurationDto>({});
  const [dynamicDisplayValue, setDynamicDisplayValue] = useState<string | undefined | null>(displayValue);

  const addChangedCompanyIds = useSearchStore((state) => state.addChangedCompanyIds);

  useEffect(() => {
    setDynamicDisplayValue(displayValue);
  }, [displayValue]);

  const { updateSelectOption, isPending } = useUpdateSelectOption();

  const [query, setQuery] = useState<string>("");
  const { data, isLoading } = useQueryHook(query.trim() === "" ? null : query, isOpen, convertedSearchParams);

  const updateOption = useCallback(
    (optionId: string) => {
      updateSelectOption(
        {
          companyId: company.id,
          option: {
            list_id: listId,
            target: target,
            target_id: optionId,
            configuration: convertedSearchParams,
            selections: buildCompanySelection(company),
          },
          queryKey: queryKey,
        },
        {
          onSuccess: () => {
            addChangedCompanyIds(company.id);
          },
        },
      );
    },
    [company, convertedSearchParams, listId, queryKey, target, updateSelectOption, addChangedCompanyIds],
  );

  const onSelected = useCallback(
    (option: OptionTypeGeneric) => {
      setSelectedId(option.id);
      setDynamicDisplayValue(buildDisplayValue(option));
      updateOption(option.id);
    },
    [updateOption],
  );

  useEffect(() => {
    if (searchParams) {
      setConvertedSearchParams(buildSearchRequestBody(searchParams));
    }
  }, [searchParams]);

  return (
    <HasMoreDataCellBase
      renderValue={renderValue}
      hasMoreData={hasMoreData}
      displayValue={dynamicDisplayValue}
      isPending={isPending}
      onOpenChange={setIsOpen}
    >
      <SelectAsyncComboBox
        options={data}
        className={"px-4"}
        placeholder={t("search.searchFor", { column: placeholder })}
        optionsClassName={"flex flex-col gap-1 max-h-72 w-[450px]"}
        renderOption={renderOption}
        isLoading={isLoading}
        onQueryUpdate={setQuery}
        selected={selectedId}
        onSelect={onSelected}
      />
    </HasMoreDataCellBase>
  );
};

export default React.memo(HasMoreDataCellGeneral) as typeof HasMoreDataCellGeneral;
